// React and Third-party imports
import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiUpload } from 'react-icons/fi'
import { IoArrowBackOutline, IoClose } from 'react-icons/io5'

// Components
import PageHeader from '../../components/Custom/PageHeader/PageHeader'
import CustomToast from '../../components/Custom/CustomToast'

// Hooks
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'

// API/Services
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

const BrandAdd = () => {
    const navigate = useNavigate()
    const GoBack = useGoBack()
    const userPermissions = useUserStore((state) => state.user) || []

    const fileInputRef = useRef(null)
    const [isUploading, setIsUploading] = useState(false)

    const [logoPreview, setLogoPreview] = useState(null)
    const [logoFile, setLogoFile] = useState(null)
    const [brandName, setBrandName] = useState('')
    const [description, setDescription] = useState('')
    const [websiteLink, setWebsiteLink] = useState('')
    const [country, setCountry] = useState('')

    const [errors, setErrors] = useState({})

    // Handle image upload
    const handleImageChange = (event) => {
        const file = event.target.files[0]
        if (file) {
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp']
            const maxSizeInMB = 5
            const maxSizeInBytes = maxSizeInMB * 1024 * 1024

            if (!validTypes.includes(file.type)) {
                setErrors((prev) => ({
                    ...prev,
                    logoFile: 'Only JPG, PNG, WEBP, and JPEG formats are allowed.',
                }))
                return
            }

            if (file.size > maxSizeInBytes) {
                setErrors((prev) => ({
                    ...prev,
                    logoFile: `File size exceeds ${maxSizeInMB}MB limit.`,
                }))
                return
            }

            setLogoFile(file)
            setLogoPreview(URL.createObjectURL(file))
            setErrors((prev) => ({ ...prev, logoFile: null }))
        }
    }

    // Handle file removal
    const handleDelete = () => {
        setLogoFile(null)
        setLogoPreview(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    // Form validation
    const validateForm = () => {
        const newErrors = {}
        if (!brandName.trim()) {
            newErrors.brandName = 'Brand name is required.'
        }
        if (!logoFile) {
            newErrors.logoFile = 'Please upload a brand logo.'
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    // Handle form submission
    const handleSubmit = async () => {
        if (!userPermissions.includes('brand:create')) {
            CustomToast({
                message: 'You do not have permission to create brands.',
                type: 'error',
            })
            return
        }
        if (!validateForm()) return

        setIsUploading(true)

        try {
            const formData = new FormData()
            formData.append('name', brandName)
            if (description) formData.append('description', description)
            if (websiteLink) formData.append('website_link', websiteLink)
            if (country) formData.append('country', country)
            formData.append('logo', logoFile)

            await axiosInterceptorInstance.post('/brands/create', formData)

            CustomToast({
                message: 'Brand created successfully.',
                type: 'success',
            })
            setBrandName('')
            setDescription('')
            setWebsiteLink('')
            setCountry('')
            handleDelete()
            navigate('/brand')
        } catch (error) {
            CustomToast({
                message: error.response?.data?.message || 'Failed to create brand. Please try again!',
                type: 'error',
            })
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <div>
            <PageHeader>
                <div className='flex gap-2 items-center justify-between w-full'>
                    <div className='flex gap-2 items-center font-medium'>
                        <button
                            onClick={GoBack}
                            className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
                        >
                            <IoArrowBackOutline />
                        </button>
                        <h1 className='text-2xl'>Add Brand</h1>
                    </div>
                </div>
            </PageHeader>

            <div className='space-y-6 flex flex-col items-center'>
                {/* Logo Upload */}
                <div className='w-full sm:w-[500px]'>
                    <label className='block text-sm font-medium mb-2'>
                        Upload Logo:
                    </label>
                    <div className='relative'>
                        <div
                            className={`relative w-full aspect-[9/5] rounded-lg bg-primary bg-opacity-5 cursor-pointer border ${errors.logoFile
                                ? 'border-red-500'
                                : 'border-dashed border-prime'
                                } flex items-center justify-center`}
                        >
                            {!logoPreview && (
                                <input
                                    accept='image/*'
                                    className='absolute inset-0 opacity-0 cursor-pointer'
                                    type='file'
                                    ref={fileInputRef}
                                    onChange={handleImageChange}
                                />
                            )}
                            {logoPreview ? (
                                <img
                                    src={logoPreview}
                                    alt='Logo Preview'
                                    className='absolute top-0 left-0 object-contain w-full h-full rounded-lg p-4'
                                />
                            ) : (
                                <div className='flex flex-col gap-4 items-center'>
                                    <FiUpload className='text-4xl text-white bg-prime rounded-full cursor-pointer p-2' />
                                    <span className='text-prime'>
                                        Only JPG, PNG, WEBP, and JPEG files are accepted
                                    </span>
                                </div>
                            )}
                            {logoFile && (
                                <button
                                    type='button'
                                    onClick={handleDelete}
                                    className='absolute top-2 right-2 flex items-center justify-center w-8 h-8 bg-prime bg-opacity-80 rounded-full text-primary bg-white'
                                >
                                    <IoClose />
                                </button>
                            )}
                        </div>
                        {errors.logoFile && (
                            <p className='text-red-500 text-sm mt-1'>{errors.logoFile}</p>
                        )}
                    </div>
                </div>

                {/* Brand Name */}
                <div className='w-full sm:w-[500px]'>
                    <label className='block text-sm font-medium mb-2'>Brand Name:</label>
                    <input
                        type='text'
                        placeholder='Enter brand name'
                        className={`w-full border ${errors.brandName ? 'border-red-500' : 'border-gray-300'
                            } rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-prime`}
                        value={brandName}
                        onChange={(e) => {
                            setBrandName(e.target.value)
                            setErrors((prev) => ({ ...prev, brandName: null }))
                        }}
                    />
                    {errors.brandName && (
                        <p className='text-red-500 text-sm mt-1'>{errors.brandName}</p>
                    )}
                </div>

                {/* Description */}
                <div className='w-full sm:w-[500px]'>
                    <label className='block text-sm font-medium mb-2'>Description (Optional):</label>
                    <textarea
                        placeholder='Enter brand description'
                        className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-prime resize-none'
                        rows='3'
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>

                {/* Website Link */}
                <div className='w-full sm:w-[500px]'>
                    <label className='block text-sm font-medium mb-2'>Website Link (Optional):</label>
                    <input
                        type='url'
                        placeholder='https://example.com'
                        className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-prime'
                        value={websiteLink}
                        onChange={(e) => setWebsiteLink(e.target.value)}
                    />
                </div>

                {/* Country */}
                <div className='w-full sm:w-[500px]'>
                    <label className='block text-sm font-medium mb-2'>Country (Optional):</label>
                    <input
                        type='text'
                        placeholder='Enter country name'
                        className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-prime'
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                    />
                </div>

                {errors?.upload && (
                    <p className='text-red-500 text-sm mt-1'>
                        {errors?.upload || 'Something went wrong. Try again'}
                    </p>
                )}

                <button
                    disabled={isUploading}
                    isLoading={isUploading}
                    onClick={handleSubmit}
                    className='w-full py-3 rounded-md sm:w-[500px] text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-prime disabled:pointer-events-none disabled:cursor-auto disabled:bg-opacity-80 disabled:text-white'
                >
                    {isUploading ? 'Creating...' : 'Create Brand'}
                </button>
            </div>
        </div>
    )
}

export default BrandAdd